import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TrackFremanItem } from 'src/app/models';
import { environment } from '../../../environments/environment';
import { FormBuilder, FormGroup } from '@angular/forms';

import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { DataService } from 'src/app/services/data.service';

interface InvoiceNotes {
  dateTime: Date;
  invNotes: any[];
}

interface TrackingEvent {
  date: Date;
  time: string;
  location: string;
  status: string;
  details?: string;
}

@Component({
  selector: 'app-track',
  templateUrl: './track.component.html',
  styleUrls: ['./track.component.scss'],
})
export class TrackComponent implements OnInit {
  @ViewChild('input')
  set input(element: ElementRef<HTMLInputElement>) {
    if (element) {
      element.nativeElement.focus();
      window.scrollTo(0, 0);
    }
  }

  errorMsg = '';
  result: TrackFremanItem = null;
  notes: any[] = [];
  shouldTrackFremanInvoice: boolean = environment.SHOULD_TRACK_FREMAN_INVOICE;
  telephone: string = environment.contact.telephone_us;
  isPackageReceived = false;
  invoiceForm: FormGroup;
  //
  historyCollapsed = false;
  canShowMoreHistory = false;

  constructor(
    private dataService: DataService,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private title: Title,
    private meta: Meta
  ) {}

  ngOnInit(): void {
    this.invoiceForm = this.formBuilder.group({
      invoice: [''],
    });

    this.route.queryParams.subscribe((params) => {
      const invoiceNo = params?.invoice;
      if (invoiceNo) {
        this.invoiceForm.get('invoice').setValue(invoiceNo);
        this.loadInvoiceStatuses();
      }
    });
  }

  loadInvoiceStatuses(): void {
    this.spinner.show();
    this.errorMsg = '';
    let invoice: string = this.invoiceForm.get('invoice').value;
    invoice = invoice.trim();
    this.dataService.getInvoiceStatuses(invoice).subscribe(
      (res: TrackFremanItem) => {
        this.result = res;
        this.changeDetector.detectChanges();
        this.spinner.hide();
        this.loadInvoiceNotes();
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.result = { invoice: null } as TrackFremanItem;
        this.errorMsg =
          'We are unable to find the invoice number you provided. Please make sure you have the correct number or you can call our help desk support at Tel. No. (707) 561-8091';
      }
    );
    console.log('result:', this.result);
  }

  loadInvoiceNotes(): void {
    if (!this.result?.invoice) return;

    this.spinner.show();
    this.errorMsg = '';
    const invoice = this.invoiceForm.get('invoice').value;
    this.dataService.getInvoiceNotes(invoice).subscribe(
      (res: InvoiceNotes) => {
        this.notes = res.invNotes;
        this.changeDetector.detectChanges();
        this.spinner.hide();
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.notes = [];
      }
    );
  }

  array_move(arr: any[], old_index: number, new_index: number) {
    if (new_index >= arr.length) {
      var k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr;
  }
  filterAdvisories(
    advisories: string[],
    keyword?: 'port' | 'release' | 'arrive'
  ): string[] {
    const tag = keyword ? `${keyword.toLowerCase()}:` : null;

    return (
      advisories
        ?.filter((a) =>
          tag
            ? a.toLowerCase().includes(tag)
            : !['port:', 'release:', 'arrive:'].some((k) =>
                a.toLowerCase().includes(k)
              )
        )
        .map((a) => (tag ? a.replace(new RegExp(tag, 'i'), '').trim() : a)) ||
      []
    );
  }

  getMainStatus(): string {
    const invoice = this.result?.invoice;
    const shipment = this.result?.shipmentH?.[0];
    const delivery = this.result?.deliveries?.[0];

    if (!invoice) return '';

    if ((invoice?.deliveredNoOfBoxes ?? 0) !== 0) {
      return 'Delivered';
    }

    if (delivery?.deliveryAttempts?.length > 0 || delivery?.deliveryAttempts)
      return 'In Transit';

    const hubTransfer = this.result?.deliveries?.find(
      (d) => d?.type === 'HubTransfer'
    );

    if (hubTransfer) {
      if (hubTransfer.inTransitDate && !hubTransfer.completedDate) {
        return 'Hub Transfer In Transit';
      }

      if (hubTransfer.completedDate && !hubTransfer.isArriveAtWarehouse) {
        return 'Hub Transfer Completed';
      }

      if (hubTransfer.isArriveAtWarehouse) {
        return 'Arrived At Hub Warehouse';
      }
    }

    const hasDeliveryAttempt =
      delivery?.deliveryAttempts && delivery.deliveryAttempts.length > 0;

    const noBoxesDelivered =
      (delivery?.completedNoOfBoxes ?? 0) === 0 &&
      (invoice?.deliveredNoOfBoxes ?? 0) === 0;

    if (hasDeliveryAttempt && noBoxesDelivered) return 'In Warehouse';

    if (shipment?.manilaPortDate) return 'Arrived At Port Of Manila';
    if (shipment?.davaoPortDate) return 'Arrived At Port Of Davao';
    if (shipment?.inTransitDate) return 'In Transit to the Philippines';
    if (shipment?.shipmentNo) return 'Loaded In Container';
    if (invoice.isInWarehouse) return 'In Warehouse';
    if (invoice.isPickupReceived) return 'Picked Up';

    return 'Processing';
  }

  getDeliveryDate(): string {
    if (!this.result?.invoice) return '';

    if (this.result.invoice.deliveredDate) {
      const date = new Date(this.result.invoice.deliveredDate);
      return (
        date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
        }) +
        ' at ' +
        date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    }

    return '';
  }

  getCurrentStatusTitle(): string {
    const status = this.getMainStatus();

    switch (status) {
      case 'Delivered':
        return 'DELIVERED';
      case 'In Transit':
        return 'IN TRANSIT';
      case 'In Warehouse':
        return 'AT WAREHOUSE';
      case 'Arrived At Port Of Manila':
        return 'ARRIVED AT PORT OF MANILA';
      case 'Arrived At Port Of Davao':
        return 'ARRIVED AT PORT OF DAVAO';
      case 'In Transit to the Philippines':
        return 'IN TRANSIT TO THE PHILIPPINES';
      case 'Loaded In Container':
        return 'LOADED IN CONTAINER';
      case 'Picked Up':
        return 'PICKED UP';
      case 'Hub Transfer In Transit':
        return 'HUB TRANSFER - IN TRANSIT';
      case 'Hub Transfer Completed':
        return 'HUB TRANSFER COMPLETED';
      case 'Arrived At Hub Warehouse':
        return 'ARRIVED AT HUB WAREHOUSE';

      default:
        return 'PROCESSING';
    }
  }

  getCurrentStatusDescription(): string {
    const status = this.getMainStatus();

    switch (status) {
      case 'Delivered':
        return 'Your package has been delivered successfully.';
      case 'In Transit':
        return 'Your package is currently in transit to the destination.';
      case 'In Warehouse':
        return 'Your package is currently at our warehouse facility.';
      case 'Arrived At Port Of Manila':
        return 'Shipment has arrived at the Port of Manila.';
      case 'Arrived At Port Of Davao':
        return 'Shipment has arrived at the Port of Davao.';
      case 'In Transit to the Philippines':
        return 'Shipment is currently in transit to the Philippines.';
      case 'Loaded In Container':
        return 'Package has been loaded into the container for shipment.';
      case 'Picked Up':
        return 'Your package has been picked up and is being processed.';
      case 'Hub Transfer In Transit':
        return 'Your package is being transferred to another hub.';
      case 'Hub Transfer Completed':
        return 'Your package has finished hub transfer processing.';
      case 'Arrived At Hub Warehouse':
        return 'Your package has arrived at the hub warehouse.';

      default:
        return 'Your package is being processed.';
    }
  }

  getRecipientName(): string {
    if (!this.result?.invoice) return '';
    return this.result.invoice.consigneeName || 'N/A';
  }

  isStepCompleted(step: string): boolean {
    if (!this.result?.invoice) return false;

    const invoice = this.result.invoice;
    const shipment = this.result.shipmentH?.[0];
    const delivery = this.result.deliveries?.[0];

    const hubTransfer = this.result?.deliveries?.find(
      (d) => d?.type === 'HubTransfer'
    );

    switch (step) {
      case 'pickup':
        return (
          invoice.isPickupReceived === true ||
          invoice.actualPickupDate != null ||
          invoice.actualDropoffDate != null
        );

      case 'intransit':
        return shipment?.inTransitDate != null;

      case 'port':
        return (
          shipment?.manilaPortDate != null || shipment?.davaoPortDate != null
        );

      case 'hubTransfer':
        return (
          hubTransfer?.inTransitDate != null ||
          hubTransfer?.completedDate != null ||
          hubTransfer?.isArriveAtWarehouse === true ||
          (delivery != null &&
            Array.isArray(delivery.deliveryAttempts) &&
            delivery.deliveryAttempts.length === 0)
        );

      case 'localDelivery':
        return (
          delivery != null &&
          Array.isArray(delivery.deliveryAttempts) &&
          delivery.deliveryAttempts.length === 0
        );

      case 'delivered':
        return (
          invoice.deliveredDate != null ||
          (invoice.deliveredNoOfBoxes ?? 0) !== 0
        );

      default:
        return false;
    }
  }

  getCompletedStepsCount(): number {
    let count = 0;

    if (this.isStepCompleted('pickup')) count++;
    if (this.isStepCompleted('intransit')) count++;
    if (this.isStepCompleted('port')) count++;
    if (this.isStepCompleted('hubTransfer')) count++;
    if (this.isStepCompleted('localDelivery')) count++;
    if (this.isStepCompleted('delivered')) count++;

    return count;
  }

  getStatusUpdates(): void {
    alert('Status updates feature not yet implemented.');
  }

  obtainProofOfDelivery(): void {
    alert('Proof of delivery feature not yet implemented.');
  }
}
